import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    getItemModel,
    deleteItemModel,
    toggleItemModelActive,
} from '../../services'
import { paths } from '@/utils/path'
import { useParams, useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { GridInformation } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '../../utils'
import { useConfirmation } from '@/contexts/confirmation'
import { Edit, Trash2 } from 'lucide-react'
import ModelSpecifications from './ModelSpecifications'

const ModelDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()

    const { data: model, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_MODELS, id],
        queryFn: () => getItemModel(id!),
    })

    const { mutate: deleteModel, isPending: isDeleting } =
        useMutationWithHandler(
            {
                mutationFn: () => deleteItemModel(id!),
            },
            {
                successMessage: 'Model deleted successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_MODELS,
                onSuccess: () => {
                    navigate(paths.warehouseModels)
                },
            }
        )

    const { mutate: toggleActive, isPending: isToggling } =
        useMutationWithHandler(
            {
                mutationFn: () => toggleItemModelActive(id!),
            },
            {
                successMessage: 'Model status updated',
                reactQueryKeys: [QUERY_KEYS.WAREHOUSE_MODELS, id],
            }
        )

    const handleDelete = () => {
        setConfirmation({
            open: true,
            title: 'Delete Model',
            description: `Are you sure you want to delete "${model?.name}"? This action cannot be undone.`,
            onConfirm: async () => {
                deleteModel()
            },
        })
    }

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    return (
        <div>
            <SubHeaders
                actions={[
                    {
                        label: model?.is_active ? 'Deactivate' : 'Activate',
                        onClick: () => toggleActive(),
                        disabled: isToggling,
                    },
                    {
                        icon: <Edit className="w-4 h-4" />,
                        label: 'Edit',
                        onClick: () =>
                            navigate(`${paths.warehouseModels}/${id}/edit`),
                    },
                    {
                        icon: <Trash2 className="w-4 h-4" />,
                        label: 'Delete',
                        onClick: handleDelete,
                        disabled: isDeleting,
                        variant: 'destructive',
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: paths.warehouseModels,
                        title: 'Item Models',
                    },
                    {
                        path: '',
                        title: model?.name || '',
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: 'Model Name',
                        value: model?.name || '-',
                    },
                    {
                        label: 'Model Code',
                        value: model?.model || '-',
                    },
                    {
                        label: 'Item Type',
                        value: (
                            <Badge
                                variant={
                                    model?.item_type === 'equipment'
                                        ? 'default'
                                        : 'secondary'
                                }
                            >
                                {model?.item_type?.toUpperCase() || ''}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Category',
                        value: model?.category?.toUpperCase() || '-',
                    },
                    {
                        label: 'Brand',
                        value: model?.brand || '-',
                        hidden: !model?.brand,
                    },
                    {
                        label: 'Capacity',
                        value: model?.capacity || '-',
                        hidden: !model?.capacity,
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={
                                    model?.is_active ? 'default' : 'destructive'
                                }
                            >
                                {model?.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Description',
                        value: model?.description || '-',
                        className: 'lg:col-span-2',
                        hidden: !model?.description,
                    },
                    {
                        label: 'Created At',
                        value: formatDateTime(model?.created_at),
                    },
                    {
                        label: 'Updated At',
                        value: formatDateTime(model?.updated_at),
                    },
                ]}
                head={{
                    title: 'Model Information',
                }}
            />

            <ModelSpecifications specifications={model?.specifications || []} />
        </div>
    )
}

export default ModelDetail
